import InteractiveService from "services/InteractiveService";
import { ELEMENT_CONFIG_TYPES, EVENT_NAMES, ACTIONS } from 'services/UndoRedoService/constants/undoRedo';
import uuid from "uuid";
import { reverse } from "./reverseFunctions/reverseFunctionMap";
import { dispatch } from 'pluginServices/store';
import { pushToast } from 'containers/ToastManager/actions';
import { CLEAR_STAGE_SELECTIONS } from 'pluginServices/editorState/constants';
import { gtmTrackAuthoringScreenUndoRedo } from "utils/gtm/handler";

const UndoRedoService = () => {

  let isRegistered = false;
  let _undoStack = [];
  let _redoStack = [];
  let _configs = { INTERACTIVE_CONFIG: [] };
  let subscribers = {};
  let _isUndoInProgress = false;
  let _isRedoInProgress = false;

  const subscribe = (eventName, callback) => {

    if (!Array.isArray(subscribers[eventName])) {
      subscribers[eventName] = [];
    }

    const id = uuid();
    const subscription = { id, callback };
    subscribers[eventName].push(subscription);

    return () => {
      unsubscribe(eventName, id);
    };

  };

  const unsubscribe = (eventName, id) => {
    if (subscribers[eventName]) {
      const index = subscribers[eventName].findIndex((sub) => sub.id === id);
      if (index !== -1) {
        subscribers[eventName].splice(index, 1);
      }
    }
  };

  const _notifyListeners = (eventName) => {
    if (subscribers[eventName]) {
      const listeners = subscribers[eventName];
      const updatedInteractiveObject = { undos: _undoStack, redos: _redoStack };
      listeners.forEach((listener) => {
        listener.callback(updatedInteractiveObject);
      });
    }
  };

  const _displayWaitToast = () => {
    dispatch(pushToast({
      message: 'Please wait',
    }));
  };

  const recordAction = async (action, config, elementId) => {
    try {
      clearRedoStack();
      const configIndex = _configs[action.elementType].push({ elementId, config }) - 1;
      const updatedAction = { elementId, ...action, configIndex };
      _undoStack.push(updatedAction);
      _notifyListeners(EVENT_NAMES.UNDO_REDO);
      return _undoStack;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("ERROR(Workspace): Could not record UndoRedo action: ", error);
    }
  };

  const clearEditorSelections = () => {
    dispatch({
      type: CLEAR_STAGE_SELECTIONS,
    });
  };

  const undoAction = async () => {
    if (_undoStack.length === 0 || _isUndoInProgress) return;

    if (_isRedoInProgress) {
      _displayWaitToast();
      return;
    }

    _isUndoInProgress = true;
    const action = _undoStack.pop();
    const previousConfig = _getPreviousConfig(action, ACTIONS.UNDO);

    await reverse[action.undo](action, ACTIONS.UNDO, previousConfig);
    _isUndoInProgress = false;
    _redoStack.push(action);
    gtmTrackAuthoringScreenUndoRedo({type:'undo',sectionId:action.elementId ?? null});
    _notifyListeners(EVENT_NAMES.UNDO_REDO);
    // Clearning component's/element's selections after they got removed form stage
    clearEditorSelections();

    return { _undoStack, _redoStack };
  };

  const redoAction = async () => {
    if (_redoStack.length === 0 || _isRedoInProgress) return;

    if (_isUndoInProgress) {
      _displayWaitToast();
      return;
    }

    _isRedoInProgress = true;
    const action = _redoStack.pop();
    const previousConfig = _getPreviousConfig(action, ACTIONS.REDO);

    await reverse[action.redo](action, ACTIONS.REDO, previousConfig);
    _isRedoInProgress = false;
    _undoStack.push(action);
    gtmTrackAuthoringScreenUndoRedo({type:'redo',sectionId:action.elementId ?? null});
    _notifyListeners(EVENT_NAMES.UNDO_REDO);
    // Clearning component's/element's selections after they got removed form stage
    clearEditorSelections();

    return { _undoStack, _redoStack };
  };

  const _getPreviousConfig = (action, eventType) => {

    let index = action.configIndex;

    if (eventType === ACTIONS.UNDO) {
      index = action.configIndex - 1;
      index = index < 0 ? 0 : index;
    }

    return _configs[action.elementType][index];
  };

  const clearRedoStack = () => {
    for (const redoData of _redoStack) {
      _configs[redoData.elementType].splice(redoData.configIndex, 1);
    }
    _redoStack = [];
  };

  const clearUndoStack = () => {
    _undoStack = [];
  };

  const clearAll = () => {
    clearUndoStack();
    clearRedoStack();
    _configs = {};
    _notifyListeners(EVENT_NAMES.UNDO_REDO);
  };

  const loadInitialConfig = async (elementType, selectedElementId, config = null) => {
    let elementConfig;
    let configPart;

    if (_configs[elementType] && _configs[elementType].find((element) => element.elementId === selectedElementId)) {
      return false;
    }

    switch (true) {
      case elementType === ELEMENT_CONFIG_TYPES.INTERACTIVE:
        elementConfig = await InteractiveService.getInteractive(selectedElementId);
        if (!elementConfig) {
          // eslint-disable-next-line no-console
          console.error(`Workspace, UndoRedoService: ${ELEMENT_CONFIG_TYPES.INTERACTIVE} failed to load`);
          return false;
        }
        configPart = { elementId: selectedElementId, config: elementConfig.config.stage };
        break;
      case elementType.includes(ELEMENT_CONFIG_TYPES.COMPONENT):
        if (!config) {
          // eslint-disable-next-line no-console
          console.error(`Workspace, UndoRedoService: ${ELEMENT_CONFIG_TYPES.COMPONENT} was null`);
          return false;
        }
        configPart = { elementId: selectedElementId, config };
        break;
      default:
        break;
    }

    if (!_configs[elementType]) {
      _configs[elementType] = [];
    }

    if (configPart) {
      _configs[elementType].push(configPart);
    }

    return true;
  };

  return {
    isRegistered: () => isRegistered,

    register: () => {
      isRegistered = true;
    },

    recordAction,
    undoAction,
    redoAction,
    loadInitialConfig,
    subscribe,
    clearAll,
  };
};

export default UndoRedoService();
